import {
    brand,
    chartSeriesColors,
    tailwindColors,
} from "../theme/gitoColors";

const axisText = tailwindColors.mute[700];
const axisLine = tailwindColors.mute[200];
const splitLine = tailwindColors.mute[100];
const bodyText = tailwindColors.mute[800];

function styleAxis(axis) {
    const base = {
        axisLabel: { color: axisText },
        axisLine: { lineStyle: { color: axisLine } },
        splitLine: { lineStyle: { color: splitLine } },
    };

    if (!axis) {
        return base;
    }

    if (Array.isArray(axis)) {
        return axis.map((item) => styleAxis(item));
    }

    return {
        ...base,
        ...axis,
        axisLabel: { ...axis.axisLabel, color: axisText },
        axisLine: {
            ...axis.axisLine,
            lineStyle: { ...axis.axisLine?.lineStyle, color: axisLine },
        },
        splitLine: {
            ...axis.splitLine,
            lineStyle: { ...axis.splitLine?.lineStyle, color: splitLine },
        },
    };
}

function unwrapChartOption(chartConfig) {
    if (!chartConfig || typeof chartConfig !== "object") {
        return {};
    }

    let option = chartConfig.echarts_option ?? chartConfig.echartsOption ?? null;
    if (typeof option === "string") {
        try {
            option = JSON.parse(option);
        } catch {
            option = null;
        }
    }
    if (option && typeof option === "object" && !Array.isArray(option)) {
        return option;
    }
    if (chartConfig.series || chartConfig.xAxis || chartConfig.dataset) {
        return chartConfig;
    }
    return {};
}

function unwrapDataArtifact(dataArtifact) {
    if (Array.isArray(dataArtifact)) {
        return { columns: [], rows: dataArtifact };
    }
    if (!dataArtifact || typeof dataArtifact !== "object") {
        return { columns: [], rows: [] };
    }
    const nested =
        dataArtifact.data && typeof dataArtifact.data === "object" && !Array.isArray(dataArtifact.data)
            ? dataArtifact.data
            : dataArtifact;
    const columns = nested.columns ?? nested.column_names ?? [];
    const rows = Array.isArray(nested.rows)
        ? nested.rows
        : Array.isArray(dataArtifact.data)
          ? dataArtifact.data
          : Array.isArray(nested.data)
            ? nested.data
            : [];
    return {
        columns: Array.isArray(columns) ? columns : [],
        rows,
    };
}

export function buildOption(chartConfig, dataArtifact) {
    const option = { ...unwrapChartOption(chartConfig) };
    const { columns, rows } = unwrapDataArtifact(dataArtifact);
    const source =
        columns.length === 0 && rows.length > 0 && Array.isArray(rows[0])
            ? rows
            : [
                  columns,
                  ...rows.map((row) =>
                      Array.isArray(row) ? row : columns.map((column) => row?.[column])
                  ),
              ];

    option.dataset = { source };
    option.backgroundColor = "transparent";
    option.color = chartSeriesColors;
    option.textStyle = {
        ...option.textStyle,
        color: bodyText,
        fontFamily: "Inter, Segoe UI, sans-serif",
    };
    option.legend = {
        ...option.legend,
        textStyle: { ...option.legend?.textStyle, color: bodyText },
    };
    option.tooltip = {
        ...option.tooltip,
        backgroundColor: brand.white,
        borderColor: tailwindColors.mute[200],
        textStyle: { ...option.tooltip?.textStyle, color: tailwindColors.mute[900] },
    };
    option.xAxis = styleAxis(option.xAxis);
    option.yAxis = styleAxis(option.yAxis);

    const hasDataZoom = Boolean(option.dataZoom);
    const legend = option.legend;
    const legendAtBottom =
        legend &&
        (legend.bottom === 0 ||
            legend.bottom === "0" ||
            String(legend.orient || "").toLowerCase() === "horizontal");

    if (hasDataZoom && legendAtBottom) {
        option.grid = {
            ...(option.grid || {}),
            bottom: option.grid?.bottom ?? 86,
        };
    }

    return option;
}

export function dataArtifactToCsv(dataArtifact) {
    const { columns, rows } = unwrapDataArtifact(dataArtifact);
    const source =
        columns.length === 0 && rows.length > 0 && Array.isArray(rows[0])
            ? rows
            : [columns, ...rows.map((row) => (Array.isArray(row) ? row : columns.map((column) => row?.[column])))];

    if (!source.length) {
        return "";
    }

    return source
        .map((line) =>
            (Array.isArray(line) ? line : [])
                .map((value) => csvEscape(value))
                .join(",")
        )
        .join("\r\n");
}

function csvEscape(value) {
    if (value == null) {
        return "";
    }
    const text = String(value);
    if (/[",\n\r]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
}

function chartConfigPrefix(name) {
    return String(name || "").replace(/_(chart|config)\.json$/i, "");
}

function chartDataPrefix(name) {
    return String(name || "").replace(/_data\.json$/i, "");
}

export function pairChartArtifacts(artifacts) {
    const charts = [
        ...artifacts.filter((artifact) => /_chart\.json$/i.test(artifact.name || "")),
        ...artifacts.filter((artifact) => /_config\.json$/i.test(artifact.name || "")),
    ];
    const dataFiles = artifacts.filter((artifact) => /_data\.json$/i.test(artifact.name || ""));
    const csvFiles = artifacts.filter((artifact) => artifact.name?.toLowerCase().endsWith(".csv"));

    const usedCharts = new Set();
    const pairs = charts
        .filter((chart) => {
            const prefix = chartConfigPrefix(chart.name);
            if (usedCharts.has(prefix)) {
                return false;
            }
            usedCharts.add(prefix);
            return true;
        })
        .map((chart) => {
            const prefix = chartConfigPrefix(chart.name);
            const data =
                dataFiles.find((file) => chartDataPrefix(file.name) === prefix) || null;
            const csv =
                csvFiles.find((file) =>
                    String(file.name || "")
                        .replace(/\.csv$/i, "")
                        .replace(/_data$/i, "") === prefix
                ) || null;
            return { chart, data, csv };
        });

    const pairedCsvNames = new Set(pairs.map((pair) => pair.csv?.name).filter(Boolean));
    const leftoverCsv = csvFiles.filter((file) => !pairedCsvNames.has(file.name));

    return { pairs, csvFiles: leftoverCsv };
}
